import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import {
  billingApi,
  BillingOverviewFilters,
  UpdateOrganizationHubspotLinkPayload,
  HubspotSearchResponse,
  HubspotCompanyHit,
  HubspotDealHit,
} from '@/api/billing';
import { projectsApi, UpdateBillingStatusPayload } from '@/api/projects';
import { organizationKeys } from '@/hooks/api/useOrganizations';
import { getErrorMessage } from '@/utils/errorUtils';

/**
 * Query key factory for the billing overview (TELV1-524)
 */
export const billingKeys = {
  all: ['billing'] as const,
  overviews: () => [...billingKeys.all, 'overview'] as const,
  overview: (filters: BillingOverviewFilters) => [...billingKeys.overviews(), filters] as const,
};

/**
 * Query key factory for HubSpot lookups
 */
export const hubspotKeys = {
  all: ['hubspot'] as const,
  status: () => [...hubspotKeys.all, 'status'] as const,
  organizationLink: (organizationId: string) =>
    [...hubspotKeys.all, 'organization-link', organizationId] as const,
  search: (kind: 'company' | 'deal', query: string) =>
    [...hubspotKeys.all, 'search', kind, query] as const,
};

/**
 * Hook to fetch the paginated org-level billing overview
 */
export function useBillingOverview(filters: BillingOverviewFilters) {
  return useQuery({
    queryKey: billingKeys.overview(filters),
    queryFn: () => billingApi.getOverview(filters),
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to update a project's billing status from the overview list. Kept
 * separate from `useUpdateBillingStatus` in `useProjects.ts` (same mutation
 * target, same partial-payload contract) purely so the overview page can
 * invalidate its own `billingKeys` alongside the project query keys.
 */
export function useUpdateBillingFromOverview() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBillingStatusPayload }) =>
      projectsApi.updateBillingStatus(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.overviews() });
      toast({
        title: 'Success',
        description: 'Billing status updated',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to update billing status'),
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to check whether HubSpot lookups are configured, plus the portal id
 * used to build deep links to companies/deals.
 */
export function useHubspotStatus() {
  return useQuery({
    queryKey: hubspotKeys.status(),
    queryFn: () => billingApi.getHubspotStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes — rarely changes
  });
}

/**
 * Hook to fetch an organization's HubSpot company link
 */
export function useOrganizationHubspotLink(organizationId: string) {
  return useQuery({
    queryKey: hubspotKeys.organizationLink(organizationId),
    queryFn: () => billingApi.getOrganizationHubspotLink(organizationId),
    enabled: !!organizationId,
    staleTime: 30000,
  });
}

/**
 * Hook to link, change, or unlink (hubspotCompanyId: null) an organization's
 * HubSpot company
 */
export function useUpdateOrganizationHubspotLink() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOrganizationHubspotLinkPayload }) =>
      billingApi.updateOrganizationHubspotLink(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hubspotKeys.organizationLink(variables.id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.overviews() });
      toast({
        title: 'Success',
        description: variables.payload.hubspotCompanyId
          ? 'HubSpot company linked'
          : 'HubSpot company unlinked',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to update HubSpot link'),
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to search HubSpot companies or deals for the linking-assist picker.
 * Backend requires `q` >= 3 chars (returns empty otherwise), so the query is
 * disabled below that to avoid noisy empty round-trips. `retry: false` keeps
 * a 429 rate limit from silently retrying — the caller surfaces it via
 * `isError`/`error`.
 */
export function useHubspotSearch(kind: 'company' | 'deal', query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: hubspotKeys.search(kind, trimmed),
    queryFn: (): Promise<HubspotSearchResponse<HubspotCompanyHit | HubspotDealHit>> =>
      kind === 'company'
        ? billingApi.searchHubspotCompanies(trimmed)
        : billingApi.searchHubspotDeals(trimmed),
    enabled: trimmed.length >= 3,
    staleTime: 10000,
    retry: false,
  });
}
